/**
 * ONE stylesheet for every printed document, in both directions.
 *
 * ── Why this is hand-written CSS and not Tailwind utilities ────────────────
 * DECISION.md §2.13 picks "HTML + Tailwind rendered to PDF by headless
 * Chromium". The Chromium half is the decision that matters and is followed
 * exactly; the Tailwind half is a build-time concern that does not survive the
 * trip. A document is rendered by a WORKER from an HTML string with no bundler
 * in the process — Tailwind v4 resolves utilities at build time by scanning
 * source, so a class written in a worker template is a class Tailwind never
 * saw, and the alternative (shipping the app's whole compiled stylesheet into
 * the worker and hoping the utilities the template used survived tree-shaking)
 * couples a PDF's correctness to the SPA's build output.
 *
 * What §2.13 actually asks for is stated in its own words: "one template, one
 * stylesheet, and one set of RTL bugs to fix rather than two." This file IS
 * that one stylesheet. It is consumed identically by the browser print path
 * and the worker, so the two cannot diverge — which is the property the
 * decision was buying.
 *
 * GEN-189 — printing shows the document and nothing else: this stylesheet
 * styles a STANDALONE page with no shell to hide, which is stronger than
 * hiding menus. GEN-191 — `thead { display: table-header-group }` repeats
 * column headings on every printed page. GEN-193/GEN-194 — the DRAFT and
 * CANCELLED stamps are the templates' `.doc-badge`. GEN-192 (a page number
 * and count on every page) is NOT built — `@page` margin counters are the
 * mechanism, flagged for the owner in the rule-queue report.
 *
 * NFR-34 — "a printed document in Arabic must print right to left, in the
 * Arabic typeface, with its numbers still left to right." The three halves
 * live here and in this package's neighbours: direction is the `dir`
 * attribute `documentDirection` puts on <html> (strings.ts) which the logical
 * properties below mirror from; the typeface is the embedded IBM Plex Sans
 * Arabic (fonts.ts); and the numbers are the `.ltr` bidi islands.
 *
 * ── The RTL rules, and why there is no `[dir=rtl]` block ───────────────────
 * There is exactly one direction-specific declaration in this file, and it is
 * on `<html>`. Everything else uses CSS LOGICAL properties — `margin-inline`,
 * `text-align: start`, `border-inline-start`, `padding-inline-end` — so the
 * browser mirrors the layout from `dir` alone. A `[dir=rtl]` override block is
 * how a stylesheet grows two sets of RTL bugs: the block drifts from the LTR
 * rules it mirrors, and every new rule has to remember it exists.
 *
 * Figures are the deliberate exception. `.ltr` pins amounts, dates and
 * document codes to left-to-right with bidi isolation, because a total is a
 * figure in every language (see format.ts on numerals).
 */
export const DOCUMENT_CSS = `
@page {
  size: A4;
  margin: 14mm 12mm 16mm;
}

* { box-sizing: border-box; }

html {
  /* THE one direction-carrying declaration; every rule below is logical. */
  font-size: 11px;
}

body {
  margin: 0;
  font-family: var(--doc-font);
  color: #111827;
  line-height: 1.45;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

/* ── Figures: LTR islands in either language ─────────────────────────────── */
.ltr {
  unicode-bidi: isolate;
  direction: ltr;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
  white-space: nowrap;
}
.num {
  font-variant-numeric: tabular-nums;
}

/* ── Header ──────────────────────────────────────────────────────────────── */
.doc-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  border-block-end: 2px solid #111827;
  padding-block-end: 10px;
  margin-block-end: 14px;
}
.doc-company { max-inline-size: 55%; }
.doc-company h1 {
  margin: 0 0 2px;
  font-size: 16px;
  font-weight: 600;
}
.doc-company .meta { color: #6b7280; font-size: 10px; }
.doc-title {
  text-align: end;
}
.doc-title h2 {
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.01em;
}
.doc-badge {
  display: inline-block;
  border: 1px solid #b91c1c;
  color: #b91c1c;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 700;
}

/* ── Field pairs ─────────────────────────────────────────────────────────── */
.doc-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px 24px;
  margin-block-end: 14px;
}
.doc-field {
  display: flex;
  gap: 8px;
  align-items: baseline;
}
.doc-field dt {
  margin: 0;
  color: #6b7280;
  min-inline-size: 92px;
}
.doc-field dd { margin: 0; font-weight: 600; }

.doc-party {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px 10px;
  margin-block-end: 14px;
}
.doc-party .label {
  color: #6b7280;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.doc-party .name { font-size: 13px; font-weight: 600; }

/* ── Tables ──────────────────────────────────────────────────────────────── */
table.doc-table {
  inline-size: 100%;
  border-collapse: collapse;
  margin-block-end: 12px;
}
table.doc-table thead th {
  background: #f3f4f6;
  border-block-end: 1px solid #d1d5db;
  padding: 5px 6px;
  font-size: 10px;
  font-weight: 600;
  text-align: start;
  white-space: nowrap;
}
table.doc-table tbody td {
  border-block-end: 1px solid #f1f5f9;
  padding: 4px 6px;
  vertical-align: top;
  text-align: start;
}
table.doc-table td.amount,
table.doc-table th.amount {
  text-align: end;
}
table.doc-table tbody tr { break-inside: avoid; }
table.doc-table thead { display: table-header-group; }
table.doc-table tfoot { display: table-footer-group; }
table.doc-table tfoot td {
  border-block-start: 1px solid #d1d5db;
  padding: 5px 6px;
  font-weight: 600;
}
.doc-empty {
  color: #6b7280;
  font-style: italic;
  padding: 10px 6px;
}

/* ── Totals rail ─────────────────────────────────────────────────────────── */
.doc-totals {
  margin-inline-start: auto;
  inline-size: 62mm;
  break-inside: avoid;
}
.doc-totals .row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 3px 0;
}
.doc-totals .row.grand {
  border-block-start: 1px solid #111827;
  border-block-end: 2px solid #111827;
  margin-block: 4px;
  padding-block: 5px;
  font-size: 13px;
  font-weight: 700;
}
.doc-totals .row.due { font-weight: 700; }

/* ── Summary cards (report centres) ──────────────────────────────────────── */
.doc-cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-block-end: 14px;
}
.doc-card {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px 10px;
}
.doc-card .label {
  color: #6b7280;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.doc-card .value { font-size: 15px; font-weight: 700; margin-block-start: 2px; }
.doc-card .delta { color: #6b7280; font-size: 9px; }

.doc-notes {
  border-inline-start: 3px solid #e5e7eb;
  padding-inline-start: 8px;
  margin-block-start: 12px;
  color: #374151;
  white-space: pre-wrap;
  break-inside: avoid;
}

/* Wave 5B — the delivery note's proof-of-delivery foot: three cells the
   receiver fills by hand when the note travels unsigned, printed values
   when it was signed for on screen. */
.doc-sign {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  margin-block-start: 18px;
  break-inside: avoid;
}
.doc-sign .cell {
  border-block-start: 1px solid #9ca3af;
  padding-block-start: 4px;
  min-height: 38px;
  font-size: 10px;
}
.doc-sign .label {
  color: #6b7280;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.doc-sign .value { font-size: 11px; font-weight: 600; margin-block-start: 2px; }
.doc-extra {
  display: inline-block;
  border: 1px solid #9ca3af;
  border-radius: 3px;
  padding: 0 4px;
  margin-inline-start: 4px;
  font-size: 8px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #374151;
}

.doc-footer {
  margin-block-start: 18px;
  padding-block-start: 6px;
  border-block-start: 1px solid #e5e7eb;
  color: #6b7280;
  font-size: 9px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

/* The Print path renders this document inside an iframe in the app; these two
   keep the browser's own print output identical to the worker's. */
@media print {
  body { margin: 0; }
  .doc-footer { position: static; }
}
`;
//# sourceMappingURL=styles.js.map